import {
  ComponentRef, Directive, ElementRef, EmbeddedViewRef, HostListener, Injector, Input, OnDestroy, ViewContainerRef
} from '@angular/core';

import { TooltipDsComponent } from '@design-system/components/templates/tooltip/tooltip.component';
import {
  TooltipDefaultValues, TooltipDisplay, TooltipLuggageConfig, TooltipPosition, TooltipPriceConfig
} from '@design-system/components/templates/tooltip/entities/tooltip.types';

@Directive({
  selector: '[appDsTooltip]',
  standalone: true
})
export class TooltipDsDirective implements OnDestroy {

  @Input() type = TooltipDefaultValues.Type;
  @Input() display = TooltipDefaultValues.Display;
  @Input() position = TooltipDefaultValues.Position;
  @Input() visible = TooltipDefaultValues.Visible;
  @Input() textAlign = TooltipDefaultValues.TextAlign;
  @Input() tooltip = '';
  @Input() price: TooltipPriceConfig | null = null;
  @Input() luggage: TooltipLuggageConfig | null = null;
  
  @HostListener('mouseenter') onMouseEnter(): void {
    if (this.display === TooltipDisplay.Auto || this.display === TooltipDisplay.Sticky) {
      this.create();
    }
  }

  @HostListener('mouseleave') onMouseLeave(): void {
    if (this.display === TooltipDisplay.Auto) {
      this.destroy();
    }
  }

  @HostListener('click') onClick(): void {
    switch (this.display) {
      case TooltipDisplay.Click:
        if (this.componentRef === null) {
          this.create();
        } else {
          this.destroy();
        }
        break;
      case TooltipDisplay.Sticky:
        this.destroy();
        break;
    }
  }

  @HostListener('window:scroll', ['$event']) onScroll(): void {
    this.offsetY = this.getOffsetY();
    this.offsetX = this.getOffsetX();
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private componentRef: ComponentRef<any> = null;
  private offsetY = 0;
  private offsetX = 0;

  constructor(
    private readonly elementRef: ElementRef,
    private readonly vcr: ViewContainerRef,
    private readonly injector: Injector
  ) {
    this.offsetY = this.getOffsetY();
    this.offsetX = this.getOffsetX();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private destroy(): void {
    if (this.componentRef !== null && this.visible) {
      this.vcr.clear();
      this.componentRef.destroy();
      this.componentRef = null;
    }
  }

  private create(): void {
    if (this.componentRef === null && this.visible) {
      const options = { injector: this.injector };
      this.componentRef = this.vcr.createComponent(TooltipDsComponent, options);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
      document.body.appendChild(domElem);
      this.setTooltipComponentProperties();
    }
  }

  private setTooltipComponentProperties(): void {
    if (this.componentRef !== null) {
      this.componentRef.instance.tooltip = this.tooltip;
      this.componentRef.instance.price = this.price;
      this.componentRef.instance.luggage = this.luggage;
      this.componentRef.instance.type = this.type;
      this.componentRef.instance.position = this.position;
      this.componentRef.instance.visible = this.visible;
      this.componentRef.instance.textAlign = this.textAlign;
      const { left, right, top, bottom } = this.elementRef.nativeElement.getBoundingClientRect();
      switch (this.position) {
        case TooltipPosition.Above:
          this.componentRef.instance.left = Math.round((right - left) / 2 + left) + Math.round(this.offsetX);
          this.componentRef.instance.top = Math.round(top - 7) + Math.round(this.offsetY);
          break;
        case TooltipPosition.Below:
          this.componentRef.instance.left = Math.round((right - left) / 2 + left) + Math.round(this.offsetX);
          this.componentRef.instance.top = Math.round(bottom) + Math.round(this.offsetY);
          break;
        case TooltipPosition.Left:
          this.componentRef.instance.left = Math.round(left) + Math.round(this.offsetX);
          this.componentRef.instance.top = Math.round(top + (bottom - top) / 2) + Math.round(this.offsetY);
          break;
        default:
          this.componentRef.instance.left = Math.round(right) + Math.round(this.offsetX);
          this.componentRef.instance.top = Math.round(top + (bottom - top) / 2) + Math.round(this.offsetY);
          break;
      }
    }
  }

  private getOffsetY(): number {
    return window?.pageYOffset || document?.documentElement?.scrollTop || document?.body?.scrollTop || 0;
  }

  private getOffsetX(): number {
    return window?.pageXOffset || document?.documentElement?.scrollLeft || document?.body?.scrollLeft || 0;
  }

}
